import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, Link2, Link2Off } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  value: string;
  onChange(html: string): void;
}

/**
 * Editor WYSIWYG mínimo (Tiptap): negrita, cursiva, links y saltos de
 * línea/párrafos. El HTML se sanea en la API al guardar. El componente se
 * remonta (vía key del padre) al cambiar de idioma para cargar el
 * contenido del locale activo.
 */
export function RichTextEditor({ value, onChange }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Solo lo básico: nada de headings, listas, blockquote, etc.
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      // Tiptap devuelve "<p></p>" cuando está vacío; lo normalizamos.
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Limpia el editor al desmontar.
  useEffect(() => () => editor?.destroy(), [editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL del enlace', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-1 border-b bg-muted/40 p-1">
        <ToolbarButton
          active={editor.isActive('bold')}
          label="Negrita"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="size-4" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          label="Cursiva"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-4" aria-hidden />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('link')} label="Enlace" onClick={setLink}>
          <Link2 className="size-4" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          active={false}
          label="Quitar enlace"
          disabled={!editor.isActive('link')}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          <Link2Off className="size-4" aria-hidden />
        </ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        className="prose-sm max-w-none px-3 py-2 [&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:outline-none [&_a]:font-medium [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2"
      />
    </div>
  );
}

interface ToolbarButtonProps {
  active: boolean;
  label: string;
  disabled?: boolean;
  onClick(): void;
  children: React.ReactNode;
}

function ToolbarButton({ active, label, disabled, onClick, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-8 items-center justify-center rounded transition-colors disabled:opacity-40',
        active ? 'bg-foreground text-background' : 'hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}
